import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";

interface Invoice {
  id: number;
  kind: "invoice" | "credit";
  number: string;
  issued_on: string;
  year: number;
  title: string;
  label: string | null;
  billed_to: string | null;
  amount: string;
  currency: string;
  pdf_url: string | null;
}

interface Props {
  currentUser: CurrentUserResource;
  currentUserLoaded: boolean;
}

const SUPPORT_EMAIL = "groundcontrol@42courses.com";

// The page's own marks, namespaced so they can't clash with the profile
// sprite, which shares ids like icon-receipt.
const InvoiceIcons = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <symbol id="inv-receipt" viewBox="0 0 16 16">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
          d="M3 2 H13 V15 L11 13.5 L9 15 L7 13.5 L5 15 L3 13.5 Z"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          d="M5 6 H11 M5 8.6 H11 M5 11.1 H8"
        />
      </symbol>
      <symbol id="inv-refund" viewBox="0 0 16 16">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5.5 4.5 L3 7 L5.5 9.5 M3 7 H10 A3 3 0 0 1 10 13 H7"
        />
      </symbol>
      <symbol id="inv-download" viewBox="0 0 14 14">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7 2 V9 M4 7 L7 10 L10 7 M2 12 H12"
        />
      </symbol>
      <symbol id="inv-help" viewBox="0 0 16 16">
        <circle
          cx="8"
          cy="8"
          r="6.3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          d="M6.2 6.3 A1.9 1.9 0 1 1 8.6 8.1 C8.1 8.3 8 8.6 8 9.2"
        />
        <circle cx="8" cy="11.3" r="0.8" fill="currentColor" />
      </symbol>
    </defs>
  </svg>
);

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const byYear = (invoices: Invoice[]) =>
  invoices.reduce<[number, Invoice[]][]>((groups, invoice) => {
    const last = groups[groups.length - 1];

    if (last && last[0] === invoice.year) {
      last[1].push(invoice);
    } else {
      groups.push([invoice.year, [invoice]]);
    }

    return groups;
  }, []);

const InvoiceRow = ({ invoice }: { invoice: Invoice }) => {
  const isCredit = invoice.kind === "credit";
  const documentName = isCredit ? "Credit note" : "Invoice";

  return (
    <li className={`invoice-row ${isCredit ? "is-credit" : ""}`}>
      <span className="invoice-ic" aria-hidden="true">
        <svg>
          <use href={isCredit ? "#inv-refund" : "#inv-receipt"} />
        </svg>
      </span>

      <div className="invoice-main">
        <span className="invoice-title">
          {invoice.title}
          {invoice.label && (
            <span className="invoice-tag">{invoice.label}</span>
          )}
        </span>
        <span className="invoice-meta">
          <span>{formatDate(invoice.issued_on)}</span>
          <span>
            {documentName} {invoice.number}
          </span>
          {invoice.billed_to && <span>Billed to {invoice.billed_to}</span>}
        </span>
      </div>

      <span className="invoice-amount">
        {isCredit && <span aria-label="minus">&minus;</span>}
        {invoice.amount}
        <span className="invoice-currency">{invoice.currency}</span>
      </span>

      {invoice.pdf_url && (
        <a
          className="invoice-pdf"
          href={invoice.pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Download ${documentName.toLowerCase()} ${invoice.number} as a PDF`}
        >
          <svg aria-hidden="true">
            <use href="#inv-download" />
          </svg>
          PDF
        </a>
      )}
    </li>
  );
};

const Invoices: React.FC<Props> = ({ currentUser, currentUserLoaded }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const signedIn = currentUser?.kind === "current-user";

  useEffect(() => {
    if (!signedIn) return;

    setLoading(true);
    getRequest("/api/invoices.json")
      .then((response: AxiosResponse) => {
        setInvoices(response.data);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [signedIn]);

  if (currentUserLoaded && !signedIn) {
    return (
      <main className="invoices-page">
        <section className="invoices-head">
          <div className="container">
            <h1>Your invoices</h1>
            <p className="invoices-sub">
              Sign in to see and download every invoice 42courses has sent you.
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="invoices-page">
      <InvoiceIcons />

      <section className="invoices-head" aria-labelledby="invoices-h1">
        <div className="container">
          <h1 id="invoices-h1">Your invoices</h1>
          <p className="invoices-sub">
            Every invoice from 42courses, in one place. Download any of them as
            a PDF for your records or an expenses claim.
          </p>
        </div>
      </section>

      <section className="invoices-body">
        <div className="container">
          {loading && (
            <div className="invoices-loading">
              <div className="ui active centered inline loader" />
              <p className="invoices-loading__note">
                Gathering your invoices. The first time can take a few seconds.
              </p>
            </div>
          )}

          {!loading && failed && (
            <div className="invoices-empty">
              <h2>We couldn't load your invoices</h2>
              <p>Please try again in a minute.</p>
            </div>
          )}

          {!loading && !failed && invoices.length === 0 && (
            <div className="invoices-empty">
              <span className="invoices-empty__icon" aria-hidden="true">
                <svg>
                  <use href="#inv-receipt" />
                </svg>
              </span>
              <h2>No invoices yet</h2>
              <p>
                When you buy a course or an All Courses Pass, its invoice will
                appear here.
              </p>
            </div>
          )}

          {!loading &&
            !failed &&
            byYear(invoices).map(([year, rows]) => (
              <section className="invoice-year" key={year}>
                <h2 className="invoice-year__heading">{year}</h2>
                <ul className="invoice-list">
                  {rows.map((invoice) => (
                    <InvoiceRow
                      invoice={invoice}
                      key={`${invoice.kind}-${invoice.id}`}
                    />
                  ))}
                </ul>
              </section>
            ))}

          {!loading && (
            <aside className="invoices-help">
              <span className="invoices-help__icon" aria-hidden="true">
                <svg>
                  <use href="#inv-help" />
                </svg>
              </span>
              <p>
                <strong>Need a company name or address on an invoice?</strong>{" "}
                Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with
                the invoice number and we'll reissue it.
              </p>
            </aside>
          )}
        </div>
      </section>
    </main>
  );
};

export default Invoices;
